import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, RefreshCw, Loader2, Send, Undo2, Timer, LayoutTemplate } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ModelDialog } from "@/components/landing/ModelDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/landing`;

export const RelatedList = ({ d, setD, field, kind, onReloaded }) => {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const ids = d[field] || [];
  useEffect(() => { axios.get(`${API}/${d.id}/related`).then((r) => setItems(r.data[kind])).catch(() => setItems([])); }, [d.id, d.updated_at]);
  const reload = async () => {
    setBusy(true);
    try { const r = await axios.post(`${API}/${d.id}/related/reload`); onReloaded(r.data); toast.success("Reîncărcat din Shopify"); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy(false);
  };
  const shown = (items || []).filter((x) => ids.includes(x.id));
  return (
    <div className="space-y-2" data-testid={`related-${kind}`}>
      {items === null ? <Loader2 className="h-4 w-4 animate-spin text-zinc-500" /> : shown.length === 0
        ? <p className="text-xs text-zinc-500" data-testid={`related-${kind}-empty`}>Nu sunt produse {kind === "similar" ? "similare" : "complementare"} active setate în Shopify pentru acest produs (cele în ciornă nu apar).</p>
        : shown.map((x) => (
          <div key={x.id} className="flex items-center gap-2 rounded-lg border border-zinc-800 p-1.5" data-testid={`related-${kind}-${x.id}`}>
            {x.image && <img src={x.image} alt="" className="h-9 w-9 rounded bg-white object-contain" />}
            <span className="flex-1 truncate text-xs text-zinc-200">{x.title}</span><span className="text-xs text-zinc-400">{x.price} lei</span>
            <button data-testid={`related-${kind}-del-${x.id}`} onClick={() => setD({ ...d, [field]: ids.filter((i) => i !== x.id) })} className="p-1 text-zinc-500 hover:text-rose-400"><X className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      <button data-testid={`related-${kind}-reload`} onClick={reload} disabled={busy} className="flex items-center gap-1 text-xs text-zinc-400 hover:text-amber-400">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Reîncarcă din Shopify
      </button>
    </div>
  );
};

const toLocal = (iso) => { if (!iso) return ""; const t = new Date(iso); return new Date(t.getTime() - t.getTimezoneOffset() * 60000).toISOString().slice(0, 16); };

export const OfferEditor = ({ d, setD }) => {
  const o = d.offer || { enabled: false, text: "", ends_at: "" };
  const set = (x) => setD({ ...d, offer: { ...o, ...x } });
  const expired = o.ends_at && new Date(o.ends_at) < new Date();
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-3" data-testid="offer-editor">
      <div className="flex items-center gap-2">
        <Switch data-testid="offer-toggle" className="data-[state=checked]:bg-amber-400 data-[state=unchecked]:bg-zinc-700" checked={!!o.enabled} onCheckedChange={(v) => set({ enabled: v })} />
        <Timer className="h-4 w-4 text-amber-400" /><span className="text-sm font-medium text-zinc-100">Bandă ofertă cu cronometru</span>
        {o.enabled && expired && <span className="text-xs text-rose-300" data-testid="offer-expired">expirată — nu se afișează</span>}
      </div>
      {o.enabled && (
        <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_200px]">
          <Input data-testid="offer-text" value={o.text} onChange={(e) => set({ text: e.target.value })} placeholder="ex. Black Friday: −20% doar până duminică" className="h-8 text-xs" />
          <Input data-testid="offer-end" type="datetime-local" value={toLocal(o.ends_at)} onChange={(e) => set({ ends_at: e.target.value ? new Date(e.target.value).toISOString() : "" })} className="h-8 text-xs" />
          <p className="text-[11px] text-zinc-500 sm:col-span-2">Banda apare sus, cu timpul rămas, și dispare singură la expirare. Reducerea propriu-zisă o setezi în Shopify.</p>
        </div>
      )}
    </div>
  );
};

export const PageChat = ({ d, beforeSend, onDone }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState("");
  const [models, setModels] = useState(false);
  const box = useRef(null);
  useEffect(() => { box.current?.scrollTo({ top: box.current.scrollHeight }); }, [d.chat?.length]);
  const call = async (key, url, body) => {
    setBusy(key);
    try { await beforeSend(); const r = await axios.post(url, body, { timeout: 150000 }); onDone(r.data); if (key === "send") setText(""); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy("");
  };
  return (
    <div className="rounded-xl border border-amber-400/30 bg-zinc-900/60" data-testid="page-chat">
      <div ref={box} className="max-h-56 space-y-2 overflow-y-auto p-3" data-testid="page-chat-messages">
        {!(d.chat || []).length && <p className="text-xs text-zinc-500">Scrie în română ce vrei schimbat pe pagină, ex. „fă titlul mai scurt și pune accent pe cadou de Crăciun” sau „adaugă ofertă Black Friday până pe 30 noiembrie”.</p>}
        {(d.chat || []).map((m, i) => (
          <p key={i} className={`max-w-[90%] whitespace-pre-wrap rounded-xl px-3 py-1.5 text-xs ${m.role === "user" ? "ml-auto bg-amber-400 text-zinc-950" : "bg-zinc-800 text-zinc-100"}`}>{m.text}</p>
        ))}
      </div>
      <div className="flex gap-2 border-t border-zinc-800 p-2">
        <Textarea data-testid="page-chat-input" value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Ce schimb pe pagină?" className="text-xs"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); text.trim() && call("send", `${API}/${d.id}/chat`, { text }); } }} />
        <div className="flex flex-col gap-1">
          <button data-testid="page-chat-send" onClick={() => text.trim() && call("send", `${API}/${d.id}/chat`, { text })} disabled={!!busy} className="flex h-9 w-10 items-center justify-center rounded-lg bg-amber-400 text-zinc-950 disabled:opacity-50">
            {busy === "send" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
          <button data-testid="page-chat-undo" title="Anulează ultima modificare din chat" onClick={() => call("undo", `${API}/${d.id}/undo`, {})} disabled={!!busy || !d.undo} className="flex h-7 w-10 items-center justify-center rounded-lg border border-zinc-700 text-zinc-300 disabled:opacity-30">
            {busy === "undo" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Undo2 className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>
      <div className="flex items-center gap-2 border-t border-zinc-800 px-2 py-1.5">
        <button data-testid="page-chat-model-btn" onClick={() => setModels(true)} className="flex items-center gap-1.5 rounded-full border border-amber-400/50 px-3 py-1 text-xs text-amber-100 hover:bg-amber-400 hover:text-zinc-950">
          <LayoutTemplate className="h-3.5 w-3.5" /> Modifică după un model (link sau PDF)
        </button>
      </div>
      {models && <ModelDialog d={d} text={text.trim()} beforeSend={beforeSend} onClose={() => setModels(false)} onDone={(x) => { onDone(x); setText(""); }} />}
    </div>
  );
};

export const VideoPicker = ({ d, setD }) => {
  const [items, setItems] = useState(null);
  useEffect(() => { axios.get(`${API}/${d.id}/videos`).then((r) => setItems(r.data.items || [])).catch(() => setItems([])); }, [d.id]);
  const ids = d.video_ids || [];
  const toggle = (id) => setD({ ...d, video_ids: ids.includes(id) ? ids.filter((x) => x !== id) : ids.length >= 4 ? ids : [...ids, id] });
  if (items === null) return <Loader2 className="h-4 w-4 animate-spin text-zinc-500" />;
  if (!items.length) return <p className="text-xs text-zinc-500" data-testid="video-picker-empty">Produsul nu are clipuri în Creative Ads. Fă un video acolo și apare aici.</p>;
  return (
    <div className="space-y-2" data-testid="video-picker">
      <p className="text-xs text-zinc-500">Bifează până la 4 clipuri din Creative Ads ({ids.length}/4). Secțiunea apare doar dacă ai ales cel puțin unul.</p>
      <div className="grid grid-cols-3 gap-2">
        {items.map((v) => (
          <button key={v.id} data-testid={`video-pick-${v.id}`} onClick={() => toggle(v.id)} className={`relative overflow-hidden rounded-lg border-2 ${ids.includes(v.id) ? "border-amber-400" : "border-zinc-800"}`}>
            <video src={v.url} muted playsInline preload="metadata" className="aspect-square w-full bg-black object-cover" />
            <span className="absolute left-1 top-1 rounded bg-zinc-950/80 px-1 text-[10px] text-zinc-200">{v.aspect_ratio || "1:1"}{v.music ? " ♪" : ""}{v.text_overlay ? " Aa" : ""}</span>
            {ids.includes(v.id) && <span className="absolute right-1 top-1 rounded-full bg-amber-400 px-1.5 text-[10px] font-bold text-zinc-950">{ids.indexOf(v.id) + 1}</span>}
          </button>
        ))}
      </div>
    </div>
  );
};
